import { Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { ReactNode } from 'react';

import { useOnceWhen } from '../../../../../../hooks';
import { trackWaitingLaneViewed } from '../../../../../../services/analytics/marketplaceEvents';
import { formatMoney } from '../../../../../../utils/helpers';
import {
  LeadGuidance,
  RangeDisplay,
  getRangeDisplay,
  shouldShowKeepTheCarCard,
} from '../../guidance';
import { payoffNoun } from '../../hooks/usePayoffDisplay';
import CostCallout from '../CostCallout';
import ExploreBuyoutButton from './ExploreBuyoutButton';
import WaitingHeader from './WaitingHeader';

type TierLanesProps = {
  guidance: LeadGuidance;
  model?: string | null;
  dealId: string;
  leadId: string;
};

const LaneLabel = ({ children }: { children: ReactNode }) => (
  <Text
    fontSize="11px"
    fontWeight="700"
    letterSpacing="0.09em"
    textTransform="uppercase"
    color="grayLight"
  >
    {children}
  </Text>
);

const LaneRange = ({
  display,
  color,
  connector,
}: {
  display: RangeDisplay;
  color: string;
  connector: string;
}) => {
  if (display.kind === 'none') return null;
  return (
    <>
      <Text
        fontSize={{ base: '23px', md: '30px' }}
        fontWeight="700"
        letterSpacing="-0.02em"
        lineHeight="1.1"
        mt={{ base: '7px', md: '10px' }}
        color={color}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {display.kind === 'range' ? `${display.low}–${display.high}` : `Up to ${display.amount}`}
      </Text>
      <Text fontSize={{ base: '11.5px', md: '12px' }} fontWeight="600" color="charcoal" mt="4px">
        {connector}
      </Text>
    </>
  );
};

const OutOfPocketCallout = ({ display }: { display: RangeDisplay }) => {
  if (display.kind === 'none') return null;
  const { prefix, amount } =
    display.kind === 'upTo'
      ? { prefix: 'up to', amount: display.amount }
      : { prefix: undefined, amount: `${display.low}–${display.high}` };
  return (
    <CostCallout
      prefix={prefix}
      amount={amount}
      footer="out of your own pocket when the sale closes"
      mt={{ base: '7px', md: '10px' }}
    />
  );
};

const LaneNote = ({ children }: { children: ReactNode }) => (
  <Text
    fontSize={{ base: '12px', md: '12.5px' }}
    color="grayLight"
    lineHeight="1.55"
    mt={{ base: '8px', md: '12px' }}
  >
    {children}
  </Text>
);

const CheckItem = ({ children }: { children: ReactNode }) => (
  <HStack as="li" align="baseline" spacing="8px">
    <Text as="span" color="leaseEndGreen" fontWeight="700" flex="none">
      ✓
    </Text>
    <Text fontSize={{ base: '11.5px', md: '12.5px' }} color="charcoal" lineHeight="1.45">
      {children}
    </Text>
  </HStack>
);

const LANE_PROPS = {
  flex: 1,
  minW: 0,
  p: { base: '15px 16px 16px', md: '22px 24px 24px' },
  align: 'flex-start',
  spacing: 0,
} as const;

// No buyout option on this tier: deliberate display cut, not an omission.
const SellNowLane = ({
  equityRange,
  payoffVerified,
}: {
  equityRange: LeadGuidance['equityRange'];
  payoffVerified: boolean;
}) => (
  <VStack {...LANE_PROPS}>
    <LaneLabel>If you sell now</LaneLabel>
    <LaneRange
      display={getRangeDisplay(equityRange, 'toYou')}
      color="leaseEndGreen"
      connector="could go straight to you"
    />
    <LaneNote>
      Estimated from your {payoffNoun(payoffVerified)} and what cars like yours sell for. Every
      offer will show you its exact math.
    </LaneNote>
    <Text
      fontSize="12.5px"
      color="charcoal"
      fontWeight="600"
      lineHeight="1.5"
      mt="14px"
      borderLeftWidth="3px"
      borderLeftColor="leaseEndYellow"
      pl="10px"
    >
      Waiting rarely pays: cars lose value faster than lease payoffs drop.
    </Text>
  </VStack>
);

const SellVsKeepLanes = ({ guidance, model, dealId, leadId }: TierLanesProps) => (
  <>
    <VStack {...LANE_PROPS}>
      <LaneLabel>If you sell</LaneLabel>
      <OutOfPocketCallout display={getRangeDisplay(guidance.equityRange, 'outOfPocket')} />
      <LaneNote>
        Cars like yours sell for a little less than your payoff, so you'd cover the gap when the
        sale closes. That's your only cost, and it replaces every end-of-lease charge:
      </LaneNote>
      <VStack as="ul" align="stretch" spacing="5px" mt="10px" listStyleType="none">
        <CheckItem>No more monthly payments</CheckItem>
        <CheckItem>No disposition fee at turn-in</CheckItem>
        <CheckItem>
          {guidance.mileageOverageEstimate != null ? (
            <>
              Skips the ~{formatMoney(guidance.mileageOverageEstimate, { noCents: true })} in
              mileage overage you're on pace for{' '}
              <Text as="span" color="grayLight" fontSize="11px">
                (estimated)
              </Text>
            </>
          ) : (
            'No mileage or wear-and-tear charges'
          )}
        </CheckItem>
      </VStack>
    </VStack>
    <VStack
      {...LANE_PROPS}
      borderTopWidth={{ base: '1px', md: 0 }}
      borderLeftWidth={{ base: 0, md: '1px' }}
      borderColor="gainsboro"
    >
      <LaneLabel>If you'd rather keep it</LaneLabel>
      <Text
        fontSize={{ base: '14px', md: '15.5px' }}
        fontWeight="700"
        color="leaseEndBlue"
        lineHeight="1.3"
        mt={{ base: '6px', md: '10px' }}
      >
        Buy out your lease instead
      </Text>
      <LaneNote>
        The lease ends and the title goes in your name. Keep driving it, no listing, no handoff.
      </LaneNote>
      <ExploreBuyoutButton
        source="lane"
        model={model}
        dealId={dealId}
        leadId={leadId}
        mt={{ base: '10px', md: '14px' }}
      />
    </VStack>
  </>
);

const TierLanes = ({ guidance, model, dealId, leadId }: TierLanesProps) => {
  useOnceWhen(leadId !== '', () => trackWaitingLaneViewed(dealId, leadId, guidance.tier));

  const shouldShowKeepLane = shouldShowKeepTheCarCard(guidance.tier);

  return (
    <VStack spacing={{ base: 5, md: 7 }} w="100%">
      <WaitingHeader
        heading="Your car is in front of buyers."
        subline={
          shouldShowKeepLane
            ? "While offers come in, here's where you stand either way."
            : "While offers come in, here's where you stand."
        }
      />

      <Flex
        direction={{ base: 'column', md: 'row' }}
        w="100%"
        maxW={shouldShowKeepLane ? '640px' : '440px'}
        bg="white"
        borderWidth="1px"
        borderColor="grayMediumBackground"
        borderRadius="14px"
        overflow="hidden"
      >
        {shouldShowKeepLane ? (
          <SellVsKeepLanes guidance={guidance} model={model} dealId={dealId} leadId={leadId} />
        ) : (
          <SellNowLane
            equityRange={guidance.equityRange}
            payoffVerified={guidance.payoffVerified}
          />
        )}
      </Flex>
    </VStack>
  );
};

export default TierLanes;
